import React, { useState, useCallback, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { user } from '../../recoil/atoms/user';

import { changePasswordAjax } from '../../services/auth/changePasswordAjax';
import { logoutAjax } from '../../services/auth/logoutAjax';

import '../../_metronic/_assets/sass/pages/login/classic/login-4.scss';
import { PasswordStrength } from '../components/PasswordStrength/PasswordStrength';
import { isPasswordValid } from 'src/common/validators';

const initialValues = {
  confirmPassword: '',
  password: '',
};

function ChangePassword({ location, handleLogin }) {
  const params = new URLSearchParams(location.search);
  const setUserAtom = useSetRecoilState(user);
  const [info, setInfo] = useState(false);

  useEffect(() => {
    logoutAjax({
      setUserAtom,
      user: params.get('u'),
      removeAllTokens: true,
    });
  }, []);

  const email = params.get('email') || '';
  const isKixieEmail = email.toLowerCase().includes('kixie.com');

  const Schema = Yup.object().shape({
    confirmPassword: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/,
        'This field is required'
      )
      .required('This field is required')
      .oneOf([Yup.ref('password'), null], 'Passwords do not match')
      .test({
        name: 'noBannedWords',
        message: 'No banned words allowed',
        test: function (password) {
          if (!isKixieEmail) return true;

          if (password) {
            const { valid } = isPasswordValid(password);
            return valid;
          }

          return false;
        },
      }),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/,
        'This field is required'
      )
      .required('This field is required')
      .test({
        name: 'noBannedWords',
        message: 'No banned words allowed',
        test: function (password) {
          if (!isKixieEmail) return true;

          if (password) {
            const { valid } = isPasswordValid(password);
            return valid;
          }

          return false;
        },
      }),
  });

  const getInputClassesConfirmPassword = () => {
    if (formik.values.confirmPassword !== '' && formik.errors.confirmPassword) {
      return 'is-invalid';
    }

    if (
      formik.values.confirmPassword !== '' &&
      !formik.errors.confirmPassword
    ) {
      return 'is-valid';
    }

    return '';
  };
  const getInputClassesPassword = () => {
    if (formik.touched.password && formik.errors.password) {
      return 'is-invalid';
    }

    if (formik.values.password !== '' && !formik.errors.password) {
      return 'is-valid';
    }

    return '';
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Schema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      const data = {
        newpass: values.password,
        user: params.get('u'),
        signature: params.get('s'),
      };
      await handlePassword(data, setStatus);
      setSubmitting(false);
      localStorage.removeItem('password_reset_required');
      resetInitialState();
    },
  });

  const handlePassword = useCallback(async (data, setStatus) => {
    const response = await changePasswordAjax(data);
    if (response.success) {
      setInfo(response.message);
      const user = {
        email: response.email,
        password: data.newpass,
      };
      await handleLogin(user);
    } else {
      setStatus(response.message ?? 'Could not update the password');
      resetInitialState();
    }
  });

  const resetInitialState = () => {
    initialValues.confirmPassword = '';
    initialValues.password = '';
  };

  return (
    <div className="login login-4 login-signin-on d-flex flex-row-fluid">
      <div
        className="d-flex flex-center flex-row-fluid bgi-size-cover bgi-position-top bgi-no-repeat"
        style={{ backgroundImage: 'url(/media/bg/bg-3.jpg)' }}
      >
        <div className="login-form p-7 position-relative overflow-hidden">
          <div className="d-flex flex-column flex-center mb-15">
            <h3 className="font-size-h1">Change Password</h3>
            <p className="text-muted font-weight-bold">Enter a new password</p>
          </div>
          <div className="login-signin">
            <form
              onSubmit={formik.handleSubmit}
              className="form fv-plugins-bootstrap fv-plugins-framework"
            >
              {formik.status && (
                <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                  <div className="alert-text font-weight-bold">
                    {formik.status}
                  </div>
                </div>
              )}

              {info && (
                <div className="mb-10 alert alert-custom alert-light-info alert-dismissible">
                  <div className="alert-text font-weight-bold">{info}</div>
                </div>
              )}

              <div className="form-group fv-plugins-icon-container">
                <input
                  placeholder="Password"
                  type="password"
                  className={`form-control h-auto form-control-solid py-4 px-8 ${getInputClassesPassword()}`}
                  name="password"
                  {...formik.getFieldProps('password')}
                />
                {formik.touched.password && formik.errors.password ? (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      {formik.errors.password}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="form-group fv-plugins-icon-container">
                <input
                  placeholder="Confirm Password"
                  type="password"
                  className={`form-control h-auto form-control-solid py-4 px-8 ${getInputClassesConfirmPassword()}`}
                  name="confirmPassword"
                  {...formik.getFieldProps('confirmPassword')}
                />
                {formik.values.confirmPassword !== '' &&
                formik.errors.confirmPassword ? (
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      {formik.errors.confirmPassword}
                    </div>
                  </div>
                ) : null}
                <div style={{ marginTop: '20px' }}>
                  <PasswordStrength
                    password={formik.values.password}
                    email={email.toLowerCase()}
                  />
                </div>
              </div>
              <div className="form-group d-flex flex-wrap justify-content-end align-items-center">
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className={'btn btn-primary font-weight-bold px-9 py-4 my-3'}
                >
                  <span>Update Password</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withRouter(ChangePassword);
